/**
 * The suggested order's ROLES and the head move's ANSWER (hermes `roadmap-score.ts`, 22 Sep 2026).
 *
 * Before roles the stored result was read positionally: entry 0 was the coaching move and every
 * entry after it was labelled as what comes next. That was wrong. Entries 1..n are where each
 * OTHER pillar would start, which is not a sequence promise — on prod, all four moves answered in
 * the week of 22 Sep were followed by the next point in the SAME pillar, not by the row the tile
 * had called "Lined up after this one". The server now says which is which:
 *
 *   head         the coaching move itself, the card's point
 *   next_up      the real successor, at most one, usually the next point in the same pillar
 *   pillar_start where another pillar would start. NOT "coming next".
 *
 * and, on the head only, what the builder already said about it, so a client never calls an
 * answered move "your next move".
 *
 * Both fields are OPTIONAL: a result stored before this shipped carries neither, and such a
 * result keeps the old positional reading. Both arrive as raw `jsonb` strings, so they are
 * parsed here rather than trusted: an unknown role is no role (legacy reading), and an unknown
 * response is still an ANSWER — the builder said something and the label must not say "next".
 * Sets, not object maps: a plain-object lookup answers `"constructor"` from `Object.prototype`.
 */

/** Where a suggested move sits in the queue, so a surface labels it truthfully. */
export type SuggestedMoveRole = 'head' | 'next_up' | 'pillar_start';

/**
 * What the builder said about the head move. Mirrors hermes `MOVE_RESPONSE_STATES`: the four
 * `coaching_prescriptions.response` values, a decline split by its reason, and `dropped` for an
 * accepted move the follow-up closed as given up.
 */
export const MOVE_RESPONSE_STATES = [
  'accepted',
  'done',
  'answered',
  'declined',
  'not_now',
  'not_my_problem',
  'already_have_it',
  'dropped',
] as const;
export type MoveResponseState = (typeof MOVE_RESPONSE_STATES)[number];

const ROLES: ReadonlySet<string> = new Set<string>(['head', 'next_up', 'pillar_start']);
const RESPONSES: ReadonlySet<string> = new Set<string>(MOVE_RESPONSE_STATES);

/**
 * One entry of the stored `suggestedMoves`, as it comes off the wire.
 *
 * `role` and `response` are plain strings on purpose. The result is read with a cast, not a
 * schema, so typing them as the unions above would be a lie about a payload a newer server may
 * widen at any time. Every reader goes through `suggestedRole` / `moveResponse`.
 */
export interface SuggestedMoveEntry {
  assetId: string;
  moveType: string;
  /** What the move is worth on the score once its point is in place; absent is 0. */
  points?: number;
  role?: string;
  response?: string | null;
}

/** The role, or null for absent and for any value this client does not know. */
export function suggestedRole(raw: unknown): SuggestedMoveRole | null {
  return typeof raw === 'string' && ROLES.has(raw) ? (raw as SuggestedMoveRole) : null;
}

/**
 * The head's answer, or null while it is open (and for an absent field).
 *
 * An unrecognised value reads `answered`, the same fallback hermes documents: something was
 * recorded, so the move is settled even when this client cannot name the answer.
 */
export function moveResponse(raw: unknown): MoveResponseState | null {
  if (typeof raw !== 'string') return null;
  const value = raw.trim();
  if (value === '') return null;
  return RESPONSES.has(value) ? (value as MoveResponseState) : 'answered';
}

/**
 * The status a resting point carries in place of its move chip. The roadmap's suggested order
 * and Home's lift tile both label a settled head from this, so the two screens say the same two
 * words about the same move.
 */
export const REST_ROW_LABEL: Record<'accepted' | 'answered', string> = {
  accepted: 'On it',
  answered: 'Answered today',
};

/** Which of the two a stored response reads as. Every answer but `accepted` is closed. */
export function settledLabel(response: MoveResponseState): string {
  return response === 'accepted' ? REST_ROW_LABEL.accepted : REST_ROW_LABEL.answered;
}

/** Does this result carry roles at all? False means read it positionally, as before. */
export function hasRoles(moves: readonly SuggestedMoveEntry[] | null | undefined): boolean {
  return (moves ?? []).some((m) => suggestedRole(m.role) !== null);
}

/** The head entry, or null — a result whose org has only a due follow-up has no head. */
export function headEntry(
  moves: readonly SuggestedMoveEntry[] | null | undefined,
): SuggestedMoveEntry | null {
  return (moves ?? []).find((m) => suggestedRole(m.role) === 'head') ?? null;
}
